import { FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  resolveDispute,
  submitFirstReview,
  submitSecondReview,
  type ModerationDetail,
} from '../../lib/moderationApi';
import {
  initialProposalState,
  proposalInput,
  proposalReady,
  ProposalEditor,
  type ProposalEditorState,
} from './ProposalEditor';
import { ErrorMessage, buttonClass, inputClass } from './ModerationUi';

export function ModerationReviewActions({
  detail,
}: {
  detail: ModerationDetail;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [secondDecision, setSecondDecision] = useState<'CONFIRM' | 'DISPUTE'>('CONFIRM');
  const [comment, setComment] = useState('');
  const [publicReason, setPublicReason] = useState('');
  const [proposal, setProposal] = useState<ProposalEditorState>(() =>
    initialProposalState(detail, detail.actions.canFirstReview)
  );

  const mutation = useMutation({
    mutationFn: async () => {
      if (detail.actions.canFirstReview) {
        return decision === 'APPROVE'
          ? submitFirstReview(detail.public_id, {
              decision,
              comment,
              proposal: proposalInput(proposal),
            })
          : submitFirstReview(detail.public_id, {
              decision,
              comment,
              public_reason: publicReason.trim() || null,
            });
      }
      if (detail.actions.canSecondReview) {
        return submitSecondReview(detail.public_id, {
          decision: secondDecision,
          comment,
          proposal_hash: detail.proposal?.payload_hash ?? '',
        });
      }
      const needsProposal = decision === 'APPROVE' && !detail.proposal;
      return decision === 'REJECT'
        ? resolveDispute(detail.public_id, {
            decision,
            comment,
            public_reason: publicReason.trim() || null,
          })
        : resolveDispute(detail.public_id, {
            decision,
            comment,
            ...(needsProposal ? { proposal: proposalInput(proposal) } : {}),
          });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['moderation', 'detail', detail.public_id], updated);
      void queryClient.invalidateQueries({ queryKey: ['moderation', 'queue'] });
      void queryClient.invalidateQueries({ queryKey: ['moderation', 'awaiting-recalc-count'] });
      setComment('');
      setPublicReason('');
    },
  });

  if (
    !detail.actions.canFirstReview &&
    !detail.actions.canSecondReview &&
    !detail.actions.canResolve
  ) {
    if (mutation.isSuccess) {
      return (
        <div role="status" className="rounded-md border border-primary/40 bg-primary/10 p-4 text-sm">
          Действие успешно выполнено.
        </div>
      );
    }
    return detail.actions.blockedReason ? (
      <div role="status" className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm">
        Действие недоступно из-за конфликта интересов или уже выполненного первого ревью.
      </div>
    ) : null;
  }

  const firstReview = detail.reviews.find((review) => review.stage === 'FIRST');
  const needsProposal =
    (detail.actions.canFirstReview && decision === 'APPROVE') ||
    (detail.actions.canResolve && decision === 'APPROVE' && !detail.proposal);
  const canSubmit =
    !needsProposal || proposalReady(proposal, detail.actions.canFirstReview);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (canSubmit && !mutation.isPending) mutation.mutate();
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {mutation.isSuccess ? (
        <div role="status" className="rounded-md border border-primary/40 bg-primary/10 p-4 text-sm">
          Действие успешно выполнено.
        </div>
      ) : null}
      {mutation.error ? (
        <div className="space-y-3">
          <ErrorMessage error={mutation.error} />
          <button
            type="button"
            className={buttonClass}
            onClick={() => {
              mutation.reset();
              void queryClient.invalidateQueries({
                queryKey: ['moderation', 'detail', detail.public_id],
              });
            }}
          >
            Обновить заявку
          </button>
        </div>
      ) : null}

      {detail.actions.canSecondReview ? (
        <>
          <div className="rounded-md border border-border bg-muted/30 p-4 text-sm">
            {detail.proposal ? (
              <>
                <p className="font-semibold">Зафиксированное предложение корректировки</p>
                <p className="mt-1 break-all font-mono text-xs">{detail.proposal.payload_hash}</p>
              </>
            ) : (
              <>
                <p className="font-semibold">Первичное решение: отказ</p>
                <p className="mt-1">
                  Публичная причина: {firstReview?.public_rejection_reason_snapshot || 'не указана'}
                </p>
              </>
            )}
          </div>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Решение второго проверяющего</span>
            <select
              className={inputClass}
              value={secondDecision}
              onChange={(event) =>
                setSecondDecision(event.target.value as 'CONFIRM' | 'DISPUTE')
              }
            >
              <option value="CONFIRM">Подтвердить зафиксированное решение</option>
              <option value="DISPUTE">Не согласен — перевести в спор</option>
            </select>
          </label>
        </>
      ) : (
        <label className="block space-y-1">
          <span className="text-sm font-medium">
            {detail.actions.canResolve ? 'Административное решение' : 'Первое решение'}
          </span>
          <select
            className={inputClass}
            value={decision}
            onChange={(event) => setDecision(event.target.value as 'APPROVE' | 'REJECT')}
          >
            <option value="APPROVE">Одобрить</option>
            <option value="REJECT">Отказать</option>
          </select>
        </label>
      )}

      {needsProposal ? (
        <ProposalEditor
          value={proposal}
          onChange={setProposal}
          requireFieldVerification={detail.actions.canFirstReview}
        />
      ) : null}

      {(detail.actions.canFirstReview || detail.actions.canResolve) && decision === 'REJECT' ? (
        <label className="block space-y-1">
          <span className="text-sm font-medium">Публичная причина отказа — необязательно</span>
          <textarea
            className={inputClass}
            maxLength={1000}
            rows={3}
            value={publicReason}
            onChange={(event) => setPublicReason(event.target.value)}
          />
          <span className="text-xs text-muted-foreground">
            Этот plain-text текст будет виден всем. Не добавляйте контактные или внутренние данные.
          </span>
        </label>
      ) : null}

      <label className="block space-y-1">
        <span className="text-sm font-medium">Внутренний комментарий — необязательно</span>
        <textarea
          className={inputClass}
          maxLength={2000}
          rows={4}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </label>
      <button type="submit" className={buttonClass} disabled={!canSubmit || mutation.isPending}>
        {mutation.isPending ? 'Сохранение…' : 'Подтвердить действие'}
      </button>
    </form>
  );
}
